// InequalitySystemPlot.js
import React, { useMemo } from "react";
import CoordinatePlane from "./CoordinatePlane";

/**
 * Each inequality is an object like:
 *   {
 *     type: ">="  or "<=",
 *     m: number,       // slope
 *     b: number,       // intercept
 *     color?: string,  // optional
 *   }
 *
 * Example: { type: ">=", m: 1, b: 1 } => y >= x + 1
 */
export default function InequalitySystemPlot({
  inequalities = [],
  xDomain = [-10, 10],
  yDomain = [-10, 10],
  width = 400,
  height = 400,
  highlightPoints = []    // <<-- new optional prop
}) {
  // 1) We'll represent the bounding box as a polygon in (x,y) coords.
  //    We'll take the corners in order: bottom-left -> bottom-right -> top-right -> top-left
  const boundingPoly = useMemo(() => {
    const [xMin, xMax] = xDomain;
    const [yMin, yMax] = yDomain;
    return [
      [xMin, yMin],
      [xMax, yMin],
      [xMax, yMax],
      [xMin, yMax],
    ];
  }, [xDomain, yDomain]);

  // 2) Clip the bounding polygon by each inequality to get intersection.
  const feasiblePoly = useMemo(() => {
    let poly = boundingPoly;
    inequalities.forEach((ineq) => {
      poly = clipPolygon(poly, ineq);
    });
    return poly;
  }, [boundingPoly, inequalities]);

  // 3) Build the "lines" array for each boundary line
  //    We'll pick any two x-values that cover the domain (like xMin..xMax).
  //    Then compute y = m*x + b at each end.  We might want dashed if it's strictly > or <.
  const lines = useMemo(() => {
    const [xMin, xMax] = xDomain;
    return inequalities.map((ineq) => {
      const { type, m, b, color } = ineq;
      const dashStyle = type.includes("=") ? "solid" : "2,2"; // or your preference
      return {
        start: [xMin, m * xMin + b],
        end: [xMax, m * xMax + b],
        color: color || "black",
        strokeDasharray: dashStyle,
      };
    });
  }, [inequalities, xDomain]);

  // 4) Return a <CoordinatePlane> with lines and a single polygon for the feasible region
  return (
    <CoordinatePlane
      width={width}
      height={height}
      xDomain={xDomain}
      yDomain={yDomain}
      showGrid
      showAxes
      lines={lines}
      disablePlot={true}
      polygons={[
        {
          vertices: feasiblePoly,
          style: { fill: "rgba(0, 0, 0, 0.15)", stroke: "none" }
        }
      ]}
      // Pass highlightPoints down
      highlightPoints={[
        { x: 0, y: 0, label: "A(0,0)", color: "blue" },
        { x: 0, y: 4, label: "B(0,4)", color: "red" },
        { x: 2, y: 4, label: "C(2,4)", color: "grey" },
        { x: 6, y: 0, label: "D(2,4)", color: "green" }
      ]}
    />
  );
}

/** 
 * Clip a polygon by a single linear inequality of the form
 *   y >= m*x + b   or   y <= m*x + b
 * We can do Sutherland-Hodgman polygon clipping:
 *   1) For consecutive pairs (p1,p2), keep the portion on the "good" side of the boundary line.
 *   2) If the segment crosses the boundary, find the intersection point.
 *   3) Return a new polygon that is wholly inside the half-plane.
 */
function clipPolygon(polygon, { type, m, b }) {
  if (!polygon || polygon.length === 0) return [];

  const isAbove = (x, y) => y - (m * x + b) >= -1e-9;  // a small epsilon
  const isBelow = (x, y) => y - (m * x + b) <= 1e-9;

  // Decide which side we want to keep
  // If type=">=", we keep points "above" y=m*x+b; if "<=", we keep "below".
  const keepSide = type === ">=" ? isAbove : isBelow;

  const clipped = [];
  for (let i = 0; i < polygon.length; i++) {
    // Current and next points
    const curr = polygon[i];
    const next = polygon[(i + 1) % polygon.length];
    const [cx, cy] = curr;
    const [nx, ny] = next;

    const currIn = keepSide(cx, cy);
    const nextIn = keepSide(nx, ny);

    if (currIn && nextIn) {
      // Both in => keep next
      clipped.push(next);
    } else if (currIn && !nextIn) {
      // Exiting => find intersection
      const inter = lineIntersect(cx, cy, nx, ny, m, b, type);
      if (inter) clipped.push(inter);
    } else if (!currIn && nextIn) {
      // Entering => push intersection + next
      const inter = lineIntersect(cx, cy, nx, ny, m, b, type);
      if (inter) clipped.push(inter);
      clipped.push(next);
    }
    // else if both out => add nothing
  }

  return clipped;
}

/**
 * Find intersection of the segment (x1,y1)->(x2,y2) with line y = m*x + b.
 * For a line of slope m, intercept b:  set param t so:
 *   (x, y) = (x1 + t*(x2 - x1), y1 + t*(y2 - y1))
 * and also y = m*x + b
 * Solve for t, then return the intersection point if 0<=t<=1.
 */
function lineIntersect(x1, y1, x2, y2, m, b, type) {
  // Parametric form for the segment
  // y(t) = y1 + t*(y2-y1)
  // x(t) = x1 + t*(x2-x1)
  //
  // we want y(t) = m*x(t) + b
  // => y1 + t*(y2 - y1) = m*(x1 + t*(x2 - x1)) + b
  // => y1 + t*dy = m*x1 + m*t*dx + b
  // => t*dy - m*t*dx = m*x1 + b - y1
  // => t*(dy - m*dx) = m*x1 + b - y1

  const dx = x2 - x1;
  const dy = y2 - y1;
  const denom = dy - m * dx;
  const rhs = m * x1 + b - y1;

  // If denom=0, segment is parallel to line => no intersection or infinite intersection
  if (Math.abs(denom) < 1e-12) {
    return null;
  }
  const t = rhs / denom;
  // Intersection point is valid if 0 <= t <= 1
  if (t < 0 || t > 1) return null;

  return [x1 + t * dx, y1 + t * dy];
}
